import React, { useState } from 'react'
import styled from '@emotion/styled'
import { Modal, Button, Notification } from '../../../components'
import subscriptionsController from '../../../api/controllers/subscriptions.controller'

const ModalContent = styled.div`
  text-align: center;
  padding: 20px;
`

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #21304e;
`

const ModalText = styled.p`
  font-size: 16px;
  color: #737991;
  margin-bottom: 24px;
  line-height: 1.5;
`

const ResetDateText = styled.p`
  font-size: 14px;
  color: #737991;
  margin-top: 16px;
  font-style: italic;
`

const ButtonsContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
`

interface IInterviewLimitModalProps {
  visible: boolean
  onClose: () => void
  hasSubscription: boolean
  interviewToken?: string
}

const InterviewLimitModal: React.FC<IInterviewLimitModalProps> = ({
  visible,
  onClose,
  hasSubscription,
  interviewToken,
}) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!visible) return null

  const getResetDate = () => {
    const now = new Date()
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1)
    return nextMonth.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const handleSubscribe = async () => {
    setLoading(true)
    setError(null)

    try {
      // Create success and cancel URLs
      const currentUrl = window.location.href
      const successUrl = interviewToken
        ? `${window.location.origin}/ava/${interviewToken}?subscription_success=true`
        : `${window.location.origin}?subscription_success=true`
      const cancelUrl = currentUrl

      const response = await subscriptionsController.createCheckoutSession(
        undefined, // Price ID is managed by backend
        successUrl,
        cancelUrl
      )

      // Redirect to Stripe Checkout
      window.location.href = response.checkout_url
    } catch (err) {
      const checkoutError = err as any
      setError(
        checkoutError.response?.data?.errors || 'Failed to create checkout session'
      )
      setLoading(false)
    }
  }

  const clearError = () => {
    setError(null)
  }

  if (hasSubscription) {
    // User has subscription but ran out of interviews
    return (
      <Modal onClose={onClose}>
        <ModalContent>
          <ModalTitle>Interview Limit Reached</ModalTitle>
          <ModalText>
            You&apos;ve used all 10 interviews this month. Your limit will reset at the
            beginning of next month.
          </ModalText>
          <ResetDateText>Limit resets on {getResetDate()}</ResetDateText>
          <ButtonsContainer>
            <Button title="Close" onClick={onClose} />
          </ButtonsContainer>
        </ModalContent>
      </Modal>
    )
  }

  // User doesn't have subscription and ran out of free interviews
  return (
    <Modal onClose={onClose}>
      <ModalContent>
        <ModalTitle>Interview Limit Reached</ModalTitle>
        <ModalText>
          You&apos;ve used your free interview this month. Subscribe to get 10 interviews
          per month with a 3-day free trial.
        </ModalText>
        {error && (
          <div style={{ marginBottom: '16px' }}>
            <Notification onClose={clearError} message={error} />
          </div>
        )}
        <ButtonsContainer>
          <Button
            title={loading ? 'Loading...' : 'Subscribe Now'}
            onClick={handleSubscribe}
            disabled={loading}
          />
          <Button title="Close" onClick={onClose} customTheme="outline" />
        </ButtonsContainer>
      </ModalContent>
    </Modal>
  )
}

InterviewLimitModal.defaultProps = {
  interviewToken: undefined,
}

export default InterviewLimitModal
